import type {
  GarmentCategory,
  WardrobeItem,
  WardrobeItemInput,
  WardrobeItemResponse,
  WardrobeItemsResponse,
} from '../../shared/wardrobe';
import { readError } from './api';
import { cacheWardrobeSnapshot, readWardrobeSnapshot } from './offlineCloset';

export interface ItemFilters {
  search?: string;
  category?: GarmentCategory | '';
  favorite?: boolean;
  archived?: boolean;
}

export async function listWardrobeItems(filters: ItemFilters = {}): Promise<WardrobeItem[]> {
  const query = new URLSearchParams();
  if (filters.search) query.set('search', filters.search);
  if (filters.category) query.set('category', filters.category);
  if (filters.favorite) query.set('favorite', 'true');
  if (filters.archived) query.set('archived', 'true');

  try {
    const response = await fetch(`/api/items${query.size ? `?${query}` : ''}`, {
      credentials: 'include',
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) throw new Error(await readError(response));
    const items = ((await response.json()) as WardrobeItemsResponse).items;
    if (!filters.search && !filters.category && !filters.favorite && !filters.archived) {
      void cacheWardrobeSnapshot(items);
    }
    return items;
  } catch (caught) {
    const snapshot = await readWardrobeSnapshot();
    if (!snapshot.length) throw caught;
    const search = filters.search?.toLocaleLowerCase('en-US');
    return snapshot.filter(
      (item) =>
        (!search ||
          [item.name, item.brand, item.primaryColor].some((value) =>
            value?.toLocaleLowerCase('en-US').includes(search),
          )) &&
        (!filters.category || item.category === filters.category) &&
        (!filters.favorite || item.favorite) &&
        (!filters.archived || item.archived),
    );
  }
}

export async function getWardrobeItem(id: string): Promise<WardrobeItem> {
  const response = await fetch(`/api/items/${encodeURIComponent(id)}`, {
    credentials: 'include',
    headers: { Accept: 'application/json' },
  });
  if (!response.ok) throw new Error(await readError(response));

  return ((await response.json()) as WardrobeItemResponse).item;
}

export async function createWardrobeItem(
  input: WardrobeItemInput,
  photo: File,
  requestKey: string = crypto.randomUUID(),
): Promise<WardrobeItem> {
  const form = new FormData();
  form.set('photo', photo);
  form.set('name', input.name);
  form.set('category', input.category);
  form.set('primaryColor', input.primaryColor);
  form.set('brand', input.brand ?? '');
  form.set('material', input.material ?? '');
  form.set('notes', input.notes ?? '');
  form.set('seasons', JSON.stringify(input.seasons));
  form.set('occasions', JSON.stringify(input.occasions));
  form.set('favorite', String(Boolean(input.favorite)));
  const thumbnail = await createUploadThumbnail(photo);
  if (thumbnail) form.set('thumbnail', thumbnail, 'thumbnail.webp');

  let response: Response;
  try {
    response = await fetch('/api/items', {
      method: 'POST',
      credentials: 'include',
      headers: {
        Accept: 'application/json',
        'X-Idempotency-Key': requestKey,
      },
      body: form,
    });
  } catch {
    throw new Error(
      'Your connection dropped. Your photo and details are still here—reconnect and retry.',
    );
  }
  if (!response.ok) throw new Error(await readError(response));

  return ((await response.json()) as WardrobeItemResponse).item;
}

async function createUploadThumbnail(photo: File): Promise<Blob | null> {
  if (
    typeof createImageBitmap !== 'function' ||
    typeof document === 'undefined' ||
    !photo.type.startsWith('image/')
  ) {
    return null;
  }
  try {
    const image = await createImageBitmap(photo);
    const maximum = 720;
    const ratio = Math.min(1, maximum / Math.max(image.width, image.height));
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(image.width * ratio));
    canvas.height = Math.max(1, Math.round(image.height * ratio));
    canvas.getContext('2d')?.drawImage(image, 0, 0, canvas.width, canvas.height);
    image.close();
    return await new Promise((resolve) => canvas.toBlob(resolve, 'image/webp', 0.82));
  } catch {
    return null;
  }
}

export async function updateWardrobeItem(
  id: string,
  patch: Partial<WardrobeItemInput> & {
    archived?: boolean;
    purchasedSize?: string | null;
    purchasedColor?: string | null;
    listedPriceMinor?: number | null;
    currency?: string | null;
  },
): Promise<WardrobeItem> {
  const response = await fetch(`/api/items/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    credentials: 'include',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(patch),
  });
  if (!response.ok) throw new Error(await readError(response));

  return ((await response.json()) as WardrobeItemResponse).item;
}

export async function deleteWardrobeItem(id: string): Promise<void> {
  const response = await fetch(`/api/items/${encodeURIComponent(id)}`, {
    method: 'DELETE',
    credentials: 'include',
    headers: { Accept: 'application/json' },
  });
  if (!response.ok) throw new Error(await readError(response));
}
